/** View models of the SKU detail (values come from the API; weeks keyed by week id). */
import { samePercent, type WaveWeek } from './model'

export type SkuType = 'Single SKU' | 'Parent' | 'Child' | 'Replen'

/** One SKU line. ``null`` means the assortment plan has no value. */
export interface SkuWaveLine {
  id: string
  sku: string
  description: string
  type: SkuType
  /** A Child's loose units count toward cube; what's in its parent's packs doesn't. */
  countsTowardCube?: boolean
  vendor: string
  stores: number
  cp: number | null
  ip: number | null
  cube: number | null
  cost: number | null
  retail: number | null
  childPurchaseUnits: number | null
  purchaseQty: number
  moq: number | null
  pogSet: number | null
  pogMax: number | null
  totalQty: number
  totalInnerPacks: number | null
  avgStoreUnits: number | null
  /** Messages of the line's warnings, shown with its comments. */
  reviewNote: string | null
  /**
   * The SKU's first wave vs its POG set: ``short`` (needs the planner), ``buyBelow``
   * (the AP buy can't cover every store) and ``raised`` (raised automatically).
   */
  pog?: { status: SkuPogStatus; message: string } | null
  /** Messages of the waves below the line's MOQ (flagged, never raised); null: none. */
  belowMoq?: string | null
  /** The line has no store model (its warning's message); null: it has one. */
  noStoreModel?: string | null
  /** What's holding up the plan's submission on this line, e.g. a missing case pack; null: nothing. */
  missingInput?: string | null
}

export type SkuPogStatus = 'short' | 'buyBelow' | 'raised'

export interface SkuWaveAllocation {
  percent: number | null
  /** Planned units (whole units, before DC rounding). */
  qty: number
  /** The wave after DC rounding (null: unknown, e.g. no DCs); adds up to ``wpQty``. */
  finalQty?: number | null
  avgStore: number | null
  cubicFtPerDc: number | null
  /** The cell holds a planner quantity (not a percent). */
  quantityOverride?: boolean
}

export interface SkuWaveColumn extends WaveWeek {
  overallPercent: number
  /** SKU-calculated (bottoms-up) share of the week (null: unknown). */
  skuPercent?: number | null
  outside: boolean
}

export interface SkuWaveRow {
  id: string
  line: SkuWaveLine | null
  /** By week id. */
  waves: Record<string, SkuWaveAllocation>
  totalQty: number
  allocatedQty: number
  totalPercent: number | null
  adjusted: boolean
  /** Week ids with a wave the overall plan does not use. */
  outsideWeeks: string[]
  needsReview: boolean
  /** Total Wave Quantity: the waves after DC rounding (null: unknown, e.g. no DCs). */
  wpQty?: number | null
  /** Total Wave Quantity minus the AP quantity (``totalQty``). */
  varianceUnits?: number | null
  /** Variance units / AP quantity (fraction). */
  variancePercent?: number | null
}

export interface SkuWaveComment {
  id: string
  content: string
  authorId: string
  authorName: string
  createdAt: string
}

/** A fractional total of 1 at the stored precision (0.0001%) is fully allocated. */
export const isBalanced = (value: number | null) =>
  value !== null && samePercent(value * 100, 100)

const divide = (value: number, denominator: number): number | null =>
  denominator > 0 ? value / denominator : null

function sumAvailable(values: (number | null)[]): number | null {
  return values.some(value => value === null)
    ? null
    : values.reduce<number>((sum, value) => sum + (value ?? 0), 0)
}

/**
 * Totals of a filtered set of rows: units exclude Parent. Cube counts what ships as
 * itself, which the server decides: a Parent, and a Child's loose units.
 */
export function aggregateSkuRows(
  rows: SkuWaveRow[],
  weeks: Pick<WaveWeek, 'id'>[]
): SkuWaveRow {
  const unitRows = rows.filter(row => row.line?.type !== 'Parent')
  const volumeRows = rows.filter(
    row => row.line?.countsTowardCube ?? row.line?.type !== 'Child'
  )
  const totalQty = unitRows.reduce((sum, row) => sum + row.totalQty, 0)
  const allocatedQty = unitRows.reduce((sum, row) => sum + row.allocatedQty, 0)
  // Rounded waves of the rows with a Total Wave Quantity: they add up to its total.
  const reconciled = unitRows.filter(row => row.wpQty != null)
  const waves: Record<string, SkuWaveAllocation> = {}
  weeks.forEach(({ id }) => {
    const qty = unitRows.reduce(
      (sum, row) => sum + (row.waves[id]?.qty ?? 0),
      0
    )
    waves[id] = {
      percent: divide(qty, totalQty),
      qty,
      // A result frozen before the rounded waves were stored has none: planned units.
      finalQty: reconciled.length
        ? reconciled.reduce(
            (sum, row) =>
              sum + (row.waves[id]?.finalQty ?? row.waves[id]?.qty ?? 0),
            0
          )
        : null,
      avgStore: sumAvailable(
        unitRows.map(row => row.waves[id]?.avgStore ?? null)
      ),
      cubicFtPerDc: sumAvailable(
        volumeRows.map(row => row.waves[id]?.cubicFtPerDc ?? null)
      ),
    }
  })
  // Reconciliation over the unit rows whose Total Wave Quantity is known.
  const wpQty = reconciled.reduce((sum, row) => sum + (row.wpQty ?? 0), 0)
  const apQty = reconciled.reduce((sum, row) => sum + row.totalQty, 0)
  return {
    id: 'filtered-totals',
    line: null,
    waves,
    totalQty,
    allocatedQty,
    totalPercent: divide(allocatedQty, totalQty),
    adjusted: false,
    outsideWeeks: [],
    needsReview: rows.some(row => row.needsReview),
    wpQty: reconciled.length ? wpQty : null,
    varianceUnits: reconciled.length ? wpQty - apQty : null,
    variancePercent: reconciled.length ? divide(wpQty - apQty, apQty) : null,
  }
}
